import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ReplayIcon from '@mui/icons-material/Replay'
import { Autocomplete, Box, IconButton, LinearProgress, styled, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MdDelete } from 'react-icons/md'
import { useGetPositionsForDropDownByMooringId } from 'src/api/positions'
import { AppTextField } from 'src/components/AppTextField'
import { EquipmentIcon } from 'src/components/customer-admin/equipments/EquipmentIcon'
import { Spinner } from 'src/components/Spinner'
import { IMainComponent } from 'src/generated-types/main-component'
import { MooringDropdownDto } from 'src/generated-types/mooring-drop-down-dto'
import { MUIThemeColors } from 'src/mui-theme'
import { bytesToSize, getThemeColor, shortenFileName } from 'src/utils'
import { getPositionName } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { UploadPhase, VideoEntry } from './BulkUploadRovDialog.action'

export interface VideoRowProps {
  video: VideoEntry
  customerId: EntityId
  localityId: EntityId
  moorings: MooringDropdownDto[]
  equipment: IMainComponent[]
  equipmentOnly?: boolean
  onUpdate: (id: string, updates: Partial<VideoEntry>) => void
  onRemove: (id: string) => void
  onRetry: (id: string) => void
  onPause: (id: string) => void
  onResume: (id: string) => void
  isSubmitting: boolean
  screenshotPreviewUrl?: string | null
  onPickFrame: (id: string) => void
  showScreenshotPicker?: boolean
}

export const VideoRow: React.FC<VideoRowProps> = ({
  video,
  customerId,
  localityId,
  moorings,
  equipment,
  equipmentOnly,
  onUpdate,
  onRemove,
  onRetry,
  onPause,
  onResume,
  isSubmitting,
  screenshotPreviewUrl,
  onPickFrame,
  showScreenshotPicker = true,
}) => {
  const { t } = useTranslation(['common', 'rov', 'position', 'main-component'])
  const theme = useTheme()

  const selectedMooring = moorings.find(m => m.id === video.mooringId) ?? null

  // Positions are filtered by mooring
  const { data: positions } = useGetPositionsForDropDownByMooringId(
    customerId,
    localityId,
    (video.mooringId ?? 0) as EntityId,
  )

  const mainComponents = equipment
  const selectedPosition = positions?.find(p => p.id === video.positionId) ?? null
  const selectedMainComponent = mainComponents.find(mc => mc.id === video.mainComponentId) ?? null

  const hasTarget = !!video.positionId || !!video.mainComponentId
  const isPickFrameEnabled =
    showScreenshotPicker &&
    screenshotPreviewUrl !== undefined &&
    !isSubmitting &&
    (video.status === 'pending' || video.status === 'error')

  const statusColor =
    video.status === 'done'
      ? 'success.main'
      : video.status === 'error'
      ? 'error.main'
      : video.status === 'uploading' || video.status === 'paused'
      ? 'info.main'
      : 'text.secondary'

  const borderColor =
    video.status === 'error'
      ? getThemeColor(theme, MUIThemeColors.errorMain)
      : video.status === 'pending' && !hasTarget
      ? getThemeColor(theme, MUIThemeColors.warningMain)
      : theme.palette.divider

  const phaseLabel = (phase: UploadPhase | null) => {
    switch (phase) {
      case 'initialize':
        return t('failed-at-init', { ns: 'rov', defaultValue: 'Failed at initialization' })
      case 'upload':
        return t('failed-at-upload', { ns: 'rov', defaultValue: 'Failed during upload' })
      case 'create-record':
        return t('failed-at-save', { ns: 'rov', defaultValue: 'Failed saving record' })
      default:
        return ''
    }
  }

  return (
    <VideoRowContainer sx={{ borderColor }} data-cy="rov-video-row">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: '14px' }}>
          <Typography variant="body2" fontWeight={500} noWrap title={video.file.name} sx={{ flex: 1, minWidth: 0 }}>
            {shortenFileName(video.file.name, 40).fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {bytesToSize(video.file.size)}
          </Typography>
          <Typography variant="caption" color={statusColor} fontWeight={600} sx={{ flexShrink: 0 }}>
            {video.status === 'done'
              ? t('completed', { ns: 'common', defaultValue: 'Done' })
              : video.status === 'uploading'
              ? `${video.progress}%`
              : video.status === 'paused'
              ? `${t('paused', { ns: 'common', defaultValue: 'Paused' })} — ${video.progress}%`
              : ''}
          </Typography>
          {video.status === 'pending' && !hasTarget && (
            <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0 }}>
              {t('select-target', { ns: 'rov', defaultValue: 'Select a position or equipment' })}
            </Typography>
          )}
          {!isSubmitting && video.status === 'error' && (
            <IconButton
              onClick={() => onRetry(video.id)}
              size="small"
              color="primary"
              title={t('retry', { ns: 'common', defaultValue: 'Retry' }) as string}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          )}
          {!isSubmitting && (video.status === 'pending' || video.status === 'error') && (
            <IconButton onClick={() => onRemove(video.id)} size="small" sx={{ flexShrink: 0 }}>
              <MdDelete fontSize={18} />
            </IconButton>
          )}
        </Box>

        {(video.status === 'uploading' || video.status === 'paused') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinearProgress variant="determinate" value={video.progress} sx={{ flex: 1, borderRadius: 1 }} />
            {video.status === 'uploading' ? (
              <IconButton
                onClick={() => onPause(video.id)}
                size="small"
                color="primary"
                title={t('pause', { ns: 'common', defaultValue: 'Pause' }) as string}
              >
                <PauseIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => onResume(video.id)}
                size="small"
                color="primary"
                title={t('resume', { ns: 'common', defaultValue: 'Resume' }) as string}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        {video.status === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" color="error">
              {phaseLabel(video.failedAtPhase)}
              {video.progress > 0 && video.failedAtPhase === 'upload' && ` (${video.progress}% uploaded)`}
            </Typography>
            {video.errorMessage && (
              <Typography variant="caption" color="error" sx={{ opacity: 0.7 }}>
                — {video.errorMessage}
              </Typography>
            )}
          </Box>
        )}

        {(video.status === 'pending' || video.status === 'error') && (
          <>
            {/* Row 1: Title + Description + screenshot preview */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <AppTextField
                  label={t('title', { ns: 'common' })}
                  value={video.title}
                  onChange={e => onUpdate(video.id, { title: e.target.value })}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                <AppTextField
                  label={t('description', { ns: 'common' })}
                  value={video.description}
                  onChange={e => onUpdate(video.id, { description: e.target.value })}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                />
              </Box>
              {showScreenshotPicker && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <ScreenshotPreviewBox
                    backgroundColor={getThemeColor(theme, MUIThemeColors.backgroundMain)}
                    isClickable={isPickFrameEnabled}
                    onClick={isPickFrameEnabled ? () => onPickFrame(video.id) : undefined}
                    title={isPickFrameEnabled ? (t('change-frame', { ns: 'rov' }) as string) : undefined}
                  >
                    {screenshotPreviewUrl === undefined && <Spinner size={16} />}
                    {screenshotPreviewUrl && (
                      <ScreenshotPreviewImage
                        src={screenshotPreviewUrl}
                        alt={t('screenshot-preview-alt', { ns: 'rov' })}
                      />
                    )}
                    {screenshotPreviewUrl === null && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', px: 0.5, fontSize: '10px' }}
                      >
                        {t('screenshot-capture-failed', { ns: 'rov' })}
                      </Typography>
                    )}
                    {isPickFrameEnabled && (
                      <ScreenshotPreviewOverlay className="change-frame-overlay">
                        <Typography variant="caption" color="common.white" sx={{ fontSize: '9px' }}>
                          {t('change-frame', { ns: 'rov' })}
                        </Typography>
                      </ScreenshotPreviewOverlay>
                    )}
                  </ScreenshotPreviewBox>
                  <IconButton
                    size="small"
                    disabled={!isPickFrameEnabled}
                    onClick={() => onPickFrame(video.id)}
                    title={t('generate-screenshot', { ns: 'rov' }) as string}
                    sx={{ p: '2px' }}
                  >
                    <ReplayIcon sx={{ fontSize: '14px' }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Row 2: Target selection — Mooring + Position OR Equipment */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {!equipmentOnly && (
                <>
                  {/* Mooring + Position group */}
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={moorings}
                      value={selectedMooring}
                      getOptionLabel={option => option?.name || ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      onChange={(_, newValue) => {
                        onUpdate(video.id, {
                          mooringId: newValue?.id ?? null,
                          positionId: null,
                          mainComponentId: null,
                        })
                      }}
                      disabled={!!video.mainComponentId || isSubmitting}
                      renderInput={params => (
                        <AppTextField {...params} label={t('mooring', { ns: 'common' })} fullWidth size="small" />
                      )}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={positions ?? []}
                      value={selectedPosition}
                      getOptionLabel={option => {
                        if (!option) return ''
                        return option.reference?.trim() ? `${option.name} (${option.reference})` : option.name
                      }}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      onChange={(_, newValue) => {
                        onUpdate(video.id, {
                          positionId: newValue?.id ?? null,
                          mainComponentId: null,
                        })
                      }}
                      disabled={!video.mooringId || !!video.mainComponentId || isSubmitting}
                      renderInput={params => (
                        <AppTextField {...params} label={t('position', { ns: 'common' })} fullWidth size="small" />
                      )}
                      size="small"
                    />
                  </Box>

                  {/* OR divider */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 1, fontWeight: 600, textTransform: 'uppercase' }}
                  >
                    {t('or', { ns: 'common', defaultValue: 'or' })}
                  </Typography>
                </>
              )}

              {/* Equipment */}
              <Box sx={{ flex: 1.5 }}>
                <Autocomplete
                  options={mainComponents}
                  value={selectedMainComponent}
                  groupBy={option => {
                    if (option.position) return getPositionName(option.position)
                    if (option.assignedServiceStationName) return option.assignedServiceStationName
                    if (option.storageName) return option.storageName
                    if (option.selectedLocalityName)
                      return `${option.selectedLocalityName} — ${t('stock', { ns: 'common', defaultValue: 'Stock' })}`
                    return t('unassigned', { ns: 'common', defaultValue: 'Unassigned' })
                  }}
                  getOptionLabel={option => {
                    if (!option) return ''
                    const typeLabel = getTranslatedText(option.type?.name) || option.typeName
                    return [typeLabel, option.serialNumber].filter(Boolean).join(' — ')
                  }}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  onChange={(_, newValue) => {
                    onUpdate(video.id, {
                      mainComponentId: newValue?.id ?? null,
                      positionId: null,
                      mooringId: null,
                    })
                  }}
                  disabled={(!equipmentOnly && !!video.mooringId) || isSubmitting}
                  renderInput={params => (
                    <AppTextField {...params} label={t('equipment', { ns: 'common' })} fullWidth size="small" />
                  )}
                  renderOption={(props, option) => {
                    const typeLabel = getTranslatedText(option.type?.name) || option.typeName
                    return (
                      <li {...props} key={option.id as number}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EquipmentIcon type={option.type?.componentType} height={24} width={24} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {[typeLabel, option.serialNumber].filter(Boolean).join(' — ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {[
                                option.name,
                                option.circumference ? `⌀${option.circumference}m` : null,
                                option.selectedLocalityName,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )
                  }}
                  size="small"
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </VideoRowContainer>
  )
}

const VideoRowContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

const ScreenshotPreviewBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'backgroundColor' && prop !== 'isClickable',
})<{ backgroundColor: string; isClickable: boolean }>(({ backgroundColor, isClickable }) => ({
  position: 'relative',
  width: '80px',
  aspectRatio: '16/9',
  flexShrink: 0,
  backgroundColor,
  borderRadius: '4px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover .change-frame-overlay': isClickable ? { opacity: 1 } : undefined,
}))

const ScreenshotPreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
})

const ScreenshotPreviewOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  opacity: 0,
  transition: 'opacity 0.15s ease-in-out',
  pointerEvents: 'none',
})
